import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import "./Header.scss";
import HCPLogo from "../../assets/images/HCPLogo.png";
import SynapseLogo from "../../assets/images/SynapseLogo.png";
import Drawer from "@mui/material/Drawer";
import { Stack, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Popover from "@mui/material/Popover";
import {
  DiscFullOutlined,
  LastPageTwoTone,
  MoreVert,
} from "@mui/icons-material";
import { Menu, MenuItem } from "@mui/material";
import NewChatMobile from "../../assets/images/NewChatMobile.svg";
import HistoryMobile from "../../assets/images/HistoryMobile.svg";
import HelpMobile from "../../assets/images/HelpMobile.svg";
import { getUserSessions } from "../../redux/slice/get-user-sessions";
import { getOktaLogoutUrl } from "../../redux/config";
import {
  clearClientAuth,
  stopSessionRefresh,
  getIncomingEmailFromToken,
  getOutgoingEmailFromToken,
} from "../../utils/common";
import {
  sendSessionEnd,
  clearSessionActivityState,
} from "../../utils/sessionActivity";
import { useDisplayIdentity } from "../../hooks/useDisplayIdentity";
import {
  UserAvatarSkeleton,
  UserNameSkeleton,
} from "../UserIdentitySkeleton/UserIdentitySkeleton";

import { ReactComponent as HomeSvg } from "../../assets/images/Home.svg";
import { ReactComponent as SalesDashboardSvg } from "../../assets/images/SalesDashboard.svg";

import SvgIcon from "@mui/material/SvgIcon";

import CloseIcon from "@mui/icons-material/Close";
import AccountCircleOutlined from "@mui/icons-material/AccountCircleOutlined";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import ImportContactsTwoToneIcon from "@mui/icons-material/ImportContactsTwoTone";
import { useEffect, useState } from "react";

/** Treats null, empty, and literal "undefined" as absent name parts */
const normalizeNamePart = (part) => {
  if (part == null) return "";
  const s = String(part).trim();
  if (!s || s.toLowerCase() === "undefined") return "";
  return s;
};

const sanitizeFullName = (full = "") =>
  full
    .trim()
    .split(/\s+/)
    .map(normalizeNamePart)
    .filter(Boolean)
    .join(" ");

/** Builds a display name, omitting last name when absent or "undefined" */
const getDisplayName = (userName, firstName, lastName) => {
  if (userName) {
    const fromUser =
      typeof userName === "string" ? sanitizeFullName(userName) : "";
    if (fromUser) return fromUser;
  }
  const full = [firstName, lastName]
    .map(normalizeNamePart)
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || "—";
};

function Header({ onLogoutOpenRep }) {
  const location = useLocation();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const openMenu = Boolean(anchorEl);
  const [open, setOpen] = useState(false);
  const [tabValue, setTabVal] = useState("");
  const [state, setState] = useState(false);

  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const profileOpen = Boolean(profileAnchorEl);
  const handleProfileOpen = (e) => setProfileAnchorEl(e.currentTarget);
  const handleProfileClose = () => setProfileAnchorEl(null);

  const handleLogout = () => {
    handleProfileClose();
    // Fire ui_session_end BEFORE clearing local state so the beacon carries
    // the current last-tab / had_prompt flags. This runs synchronously via
    // navigator.sendBeacon so it survives the immediate redirect below.
    //
    // Resolve last_tab the same way App.js does for the pagehide listener:
    // on the homepage use the ?tab= URL param (performance | repassist | qbr);
    // on non-homepage routes (/training, /no-access, /error) fall back to the
    // pathname so last_tab doesn't default to "performance" when the user is
    // actually somewhere else.
    const currentTab = (() => {
      if (location.pathname === "/") {
        return (
          new URLSearchParams(location.search).get("tab") || "performance"
        );
      }
      const label = location.pathname
        .replace(/^\/+/, "")
        .replace(/\/+$/, "");
      return label || "unknown";
    })();
    sendSessionEnd("logout", currentTab);
    clearSessionActivityState();
    stopSessionRefresh();
    clearClientAuth();
    const logoutUrl = getOktaLogoutUrl();
    window.location.replace(logoutUrl);
  };

  const userName = useSelector((state) => state.userName.userName);
  const {
    initials,
    fullName: displayFullName,
    isLoading: displayIdentityLoading,
  } = useDisplayIdentity();

  const incomingEmail = getIncomingEmailFromToken();
  const outgoingEmail = getOutgoingEmailFromToken();
  const userEmail = incomingEmail || outgoingEmail;
  const displayName = displayFullName || getDisplayName(userName, "", "");

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleRedirectNewChatOpen = () => {};
  const toggleDrawerOpen = (event, value) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    handleClose();
    setTabVal(value);
    setState(true);
  };
  const toggleDrawerClose = () => {
    setState(false);
  };

  // Side Main Nav
  const navItems = [
    {
      label: "Home",
      path: "/",
      icon: HomeSvg,
      size: { w: 20, h: 17 },
      external: false,
    },
    {
      label: "Sales Dashboard",
      path: "https://us-east-1.online.tableau.com/t/unither/views/Sales2_0Dashboard_PRODRLS/Home-Summary?%3Aorigin=card_share_link&%3Alinktarget=_parent&%3Aembed=yes#1",
      icon: SalesDashboardSvg,
      size: { w: 18, h: 18 },
      external: true,
    },
    {
      label: "Training & Resources",
      path: "https://unithercloud.sharepoint.com/sites/commtech#",
      icon: ImportContactsTwoToneIcon,
      size: { w: 21, h: 24 },
      external: true,
      isMuiIcon: true,
    },
  ];

  const [navOpen, setNavOpen] = useState(false);
  const toggleNavDrawer = (next) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      "key" in event &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setNavOpen(next);
  };

  const [activeLabel, setActiveLabel] = useState(null);
  useEffect(() => {
    setActiveLabel("Home");
  }, [location.pathname, location.search]);

  const navigate = useNavigate();

  const handleNavClick = (item) => {
    setActiveLabel(item.label);

    if (item.external) {
      window.open(item.path, "_blank", "noopener,noreferrer");
    } else {
      const params = new URLSearchParams(location.search);
      params.set("tab", "performance");
      navigate({ pathname: "/", search: params.toString() });
    }

    setNavOpen(false);
  };

  return (
    <AppBar position="fixed" className="supply-chain-header">
      <Container
        sx={{ padding: "0 !important", maxWidth: "100% !important" }}
        className="supply-chain-container"
      >
        <Toolbar sx={{ p: 0 }} className="supply-chain-toolbar">
          <IconButton
            edge="start"
            aria-label="Open main navigation"
            onClick={toggleNavDrawer(true)}
            className="global-nav-trigger"
            sx={{ ml: 1 }}
          >
            <MenuIcon sx={{ fontSize: "32px" }} />
          </IconButton>
          <div className="logo-holder">
            <img
              src={SynapseLogo}
              alt="logo"
              className="supply-chain-logo"
              style={{
                alignSelf: "baseline",
              }}
            />
          </div>
          <MenuIcon
            sx={{ marginLeft: "10px" }}
            onClick={toggleDrawer(true)}
            className="supply-chain-hamburger"
          />
          <Box
            className="supply-chain-header-box"
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-end",
              flexWrap: "nowrap",
              marginLeft: "auto",
              padding: 0,
              maxWidth: "100%",
              height: "60px",
            }}
          >
            <IconButton
              sx={{ p: 0 }}
              onClick={handleProfileOpen}
              aria-controls={profileOpen ? "profile-popover" : undefined}
              aria-haspopup="true"
              aria-expanded={profileOpen ? "true" : undefined}
            >
              {displayIdentityLoading ? (
                <UserAvatarSkeleton size={40} />
              ) : (
                <Avatar
                  sx={{
                    bgcolor: "#E56800",
                    color: "#FFFFFF",
                    fontWeight: "700",
                    fontSize: "16px",
                  }}
                >
                  {initials}
                </Avatar>
              )}
            </IconButton>
          </Box>
        </Toolbar>
      </Container>

      <Drawer
        anchor="left"
        open={navOpen}
        onClose={toggleNavDrawer(false)}
        PaperProps={{ sx: { width: 300 } }}
      >
        <Box
          role="presentation"
          sx={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", p: 1.25 }}>
            <IconButton
              onClick={toggleNavDrawer(false)}
              aria-label="Close menu"
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Divider />

          <List sx={{ py: 0 }}>
            {navItems.map((item) => {
              const isActive = activeLabel === item.label;
              const IconComponent = item.icon;

              return (
                <ListItem key={item.label} disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => handleNavClick(item)}
                    sx={{
                      mx: 2,
                      mt: 2,
                      borderRadius: 1,
                      "&.Mui-selected": { bgcolor: "#E56800", color: "#fff" },
                      "&.Mui-selected:hover": { bgcolor: "#E56800" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 36,
                        color: isActive ? "#FFFFFF" : "#535252",
                      }}
                    >
                      {item.isMuiIcon ? (
                        <IconComponent
                          sx={{
                            width: item.size.w,
                            height: item.size.h,
                            color: "inherit",
                          }}
                        />
                      ) : (
                        <SvgIcon
                          component={item.icon}
                          inheritViewBox
                          sx={{
                            width: item.size.w,
                            height: item.size.h,
                            "& path": { fill: "currentColor" },
                            "& [stroke]": { stroke: "currentColor" },
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: 14,
                        fontWeight: isActive ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>

          <Box sx={{ flexGrow: 1 }} />
        </Box>
      </Drawer>

      <Drawer
        open={open}
        onClose={toggleDrawer(false)}
        className="supply-chain-hamburger-drawer"
      >
        <Stack
          direction="row"
          spacing={2}
          className="supply-chain-stack-with-avatar"
        >
          {displayIdentityLoading ? (
            <UserAvatarSkeleton size={32} />
          ) : (
            <Avatar
              sx={{
                bgcolor: "#E56800",
                color: "white",
                fontWeight: "700",
                fontSize: "14px",
                width: "32px",
                height: "32px",
              }}
            >
              {initials}
            </Avatar>
          )}
        </Stack>
      </Drawer>

      <Menu
        className="mobile-menu-parent"
        id="basic-menu"
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "basic-button",
        }}
      >
        <MenuItem
          className="mobile-menu-item"
          onClick={() => {
            handleRedirectNewChatOpen();
          }}
        >
          <img src={NewChatMobile} alt="new-chat-mobile-icon" />
          &nbsp; New Session
        </MenuItem>
        <MenuItem
          className="mobile-menu-item"
          onClick={(e) => toggleDrawerOpen(e, "history")}
        >
          <img src={HistoryMobile} alt="history-icon" />
          &nbsp; View History
        </MenuItem>
        <MenuItem
          className="mobile-menu-item"
          onClick={(e) => toggleDrawerOpen(e, "help")}
        >
          <img src={HelpMobile} alt="info-icon" />
          &nbsp; Help
        </MenuItem>
      </Menu>

      <Popover
        id="profile-popover"
        open={profileOpen}
        anchorEl={profileAnchorEl}
        onClose={handleProfileClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: "4px",
              border: "1px solid #EEEEEE",
              width: 300,
              boxShadow: "0px 4px 15px 0px #00000026",
            },
          },
        }}
      >
        <Box sx={{ p: 2, display: "flex", gap: 1.5, alignItems: "center" }}>
          {displayIdentityLoading ? (
            <UserAvatarSkeleton size={40} />
          ) : (
            <Avatar
              sx={{
                bgcolor: "#E56800",
                color: "#FFFFFF",
                fontWeight: "700",
                fontSize: "16px",
              }}
            >
              {initials}
            </Avatar>
          )}
          <Box sx={{ minWidth: 0 }}>
            {displayIdentityLoading ? (
              <UserNameSkeleton width={140} height={24} />
            ) : (
              <Typography variant="subtitle1" noWrap fontWeight={600}>
                {displayName}
              </Typography>
            )}
            <Typography variant="body2" color="text.secondary" noWrap>
              {userEmail}
            </Typography>
          </Box>
        </Box>

        <Divider />

        <List sx={{ py: 0 }}>
          <ListItem disablePadding>
            <ListItemButton onClick={handleLogout}>
              <ListItemText primary="Logout" />
            </ListItemButton>
          </ListItem>
        </List>
      </Popover>
    </AppBar>
  );
}
export default Header;